/* ==========================================================================
   3afret Electronics — facets.js
   Collection & search filtering + sorting using Shopify's native facets
   plus the Section Rendering API, so results update without a full reload.
   ========================================================================== */
(function () {
  'use strict';

  const Theme = (window.Theme = window.Theme || {});

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.grid = document.querySelector('[data-product-grid]');

      this.onFormInput = Theme.debounce((event) => {
        // Price inputs get a longer debounce; checkboxes apply immediately.
        this.apply(event.target.closest('form'));
      }, 320);

      // Bound to the document, not to this element: the filter form may live in
      // its own section, which is a sibling of this one rather than a child.
      // [data-facets-form] is specific to Shopify's native facets, so the
      // suggestion panel's own [data-fb-facets-form] is unaffected.
      document.addEventListener('change', (event) => {
        const form = event.target.closest('[data-facets-form]');
        if (!form) return;
        if (event.target.type === 'number') return; // handled on input
        this.apply(form);
      });

      document.addEventListener('input', (event) => {
        if (event.target.type !== 'number') return;
        if (!event.target.closest('[data-facets-form]')) return;
        this.onFormInput(event);
      });

      document.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-facets-form]');
        if (!form) return;
        event.preventDefault();
        this.apply(form);
      });

      // Sort dropdown lives outside the filter form.
      document.querySelectorAll('[data-sort-select]').forEach((select) => {
        select.addEventListener('change', () => {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', select.value);
          url.searchParams.delete('page');
          this.navigate(url.toString());
        });
      });

      // Active-filter pills and "clear all" links.
      document.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facet-remove]');
        if (!link) return;
        event.preventDefault();
        this.navigate(link.href);
      });

      window.addEventListener('popstate', () => this.navigate(window.location.href, false));
    }

    buildUrl(form) {
      const data = new FormData(form);
      const params = new URLSearchParams();

      for (const [key, value] of data.entries()) {
        if (value === '' || value === null) continue;
        params.append(key, value);
      }

      // Preserve the current sort order.
      const sort = new URL(window.location.href).searchParams.get('sort_by');
      if (sort && !params.has('sort_by')) params.set('sort_by', sort);

      const base = window.location.pathname;
      const query = params.toString();
      return query ? `${base}?${query}` : base;
    }

    apply(form) {
      if (!form) return;
      this.navigate(this.buildUrl(form));
    }

    async navigate(url, push = true) {
      const grid = document.querySelector('[data-product-grid]');
      if (grid) grid.classList.add('product-grid--loading');
      this.classList.add('facets-form--loading');

      try {
        const target = new URL(url, window.location.origin);

        // A standalone filters section has to be re-rendered too, otherwise its
        // counts and active states go stale while the grid updates.
        const ids = [this.sectionId];
        document.querySelectorAll('[data-facets-section]').forEach((node) => {
          const id = node.dataset.facetsSection;
          if (id && !ids.includes(id)) ids.push(id);
        });
        target.searchParams.set('sections', ids.join(','));

        const response = await fetch(target.toString());
        const payload = await response.json();

        // One document per returned section; each replaces whatever it contains.
        Object.values(payload).forEach((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          this.replace(doc, '[data-product-grid-wrapper]');
          this.replace(doc, '[data-active-facets]');
          this.replace(doc, '[data-facets-desktop]');
          this.replace(doc, '[data-facets-mobile]');
          // The standalone bar uses its own hooks so it can never overwrite the
          // in-grid sidebar, or be overwritten by it, when both are present.
          this.replace(doc, '[data-facets-bar]');
          this.replace(doc, '[data-facets-bar-mobile]');
          this.replace(doc, '[data-collection-count]');
        });

        if (push) {
          const clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          window.history.pushState({}, '', clean.toString());
        }

        const anchor = document.querySelector('[data-results-top]');
        if (anchor && push) {
          const top = anchor.getBoundingClientRect().top + window.scrollY - 90;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
        document.dispatchEvent(new CustomEvent('facets:updated'));
      } catch (error) {
        // Fall back to a normal page load rather than leaving a stale grid.
        window.location.href = url;
      } finally {
        const g = document.querySelector('[data-product-grid]');
        if (g) g.classList.remove('product-grid--loading');
        this.classList.remove('facets-form--loading');
      }
    }

    replace(doc, selector) {
      const source = doc.querySelector(selector);
      const targets = document.querySelectorAll(selector);
      if (!source || !targets.length) return;
      targets.forEach((target) => {
        // Keep <details> open/closed state so the sidebar doesn't jump around.
        const openStates = new Map();
        target.querySelectorAll('details[data-facet-key]').forEach((details) => {
          openStates.set(details.dataset.facetKey, details.open);
        });
        target.innerHTML = source.innerHTML;
        target.querySelectorAll('details[data-facet-key]').forEach((details) => {
          if (openStates.has(details.dataset.facetKey)) details.open = openStates.get(details.dataset.facetKey);
        });
      });
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* Chip popovers in the standalone filter bar. `toggle` does not bubble, so it
     is caught in the capture phase. Panel open state survives a re-render
     because replace() restores it per data-facet-key. */
  document.addEventListener(
    'toggle',
    (event) => {
      const chip = event.target;
      if (!chip.matches || !chip.matches('.filters-bar .facet') || !chip.open) return;
      const bar = chip.closest('.filters-bar');
      if (!bar) return;
      bar.querySelectorAll('.facet[open]').forEach((other) => {
        if (other !== chip) other.open = false;
      });
    },
    true
  );

  document.addEventListener('click', (event) => {
    document.querySelectorAll('.filters-bar .facet[open]').forEach((chip) => {
      if (!chip.contains(event.target)) chip.open = false;
    });
  });
})();
